// Design tokens - Crisp White & Vibrant Blue Palette
const C = {
  bg: '#f8fafc',
  surface: '#ffffff',
  surfaceHover: '#f1f5f9',
  border: '#e2e8f0',
  borderFocus: '#3b82f6',
  accent: '#2563eb',
  accentDim: '#eff6ff',
  accentGrad: 'linear-gradient(135deg,#3b82f6 0%,#1d4ed8 100%)',
  green: '#16a34a',
  greenDim: '#f0fdf4',
  red: '#dc2626',
  redDim: '#fef2f2',
  yellow: '#d97706',
  yellowDim: '#fffbeb',
  purple: '#7c3aed',
  purpleDim: '#f5f3ff',
  text: '#0f172a',
  textMuted: '#64748b',
  textSub: '#475569',
};

export const S = {
  // Layout
  page: {
    minHeight: '100vh',
    background: '#f8fafc',
    color: C.text,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    padding: '24px 20px 80px',
  },
  shell: {
    maxWidth: 1120,
    margin: '0 auto',
    display: 'grid',
    gap: 18,
  },

  // Cards
  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 20,
    padding: '28px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  },
  cardAccent: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 14,
    padding: '14px 18px',
  },
  cardGreen: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 14,
    padding: '14px 18px',
  },

  // Typography
  label: {
    display: 'block',
    fontWeight: 700,
    marginBottom: 7,
    color: C.accent,
    fontSize: 12,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  sectionTitle: {
    margin: '0 0 18px',
    fontSize: 16,
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '0.01em',
  },
  muted: { color: C.textMuted, fontSize: 13 },

  // Form elements
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: '#ffffff',
    color: C.text,
    marginBottom: 14,
    boxSizing: 'border-box',
    fontSize: 14,
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  textarea: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    background: '#ffffff',
    color: C.text,
    minHeight: 150,
    resize: 'vertical',
    boxSizing: 'border-box',
    fontSize: 14,
    lineHeight: 1.6,
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },

  // Buttons
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 24px',
    borderRadius: 12,
    border: 'none',
    background: C.accentGrad,
    color: '#ffffff',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: 14,
    letterSpacing: '0.01em',
    boxShadow: '0 4px 20px rgba(79,209,255,0.25)',
  },
  btnSm: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    borderRadius: 999,
    border: `1px solid rgba(255,255,255,0.14)`,
    background: 'rgba(255,255,255,0.04)',
    color: C.text,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
  },
  btnActive: {
    background: C.accentDim,
    borderColor: 'rgba(79,209,255,0.35)',
    color: C.accent,
  },
  btnDanger: {
    background: 'rgba(248,113,113,0.1)',
    borderColor: 'rgba(248,113,113,0.3)',
    color: C.red,
  },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed', transform: 'none' },

  // File upload button
  fileBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '8px 16px',
    borderRadius: 999,
    border: `1px solid rgba(79,209,255,0.25)`,
    background: 'rgba(79,209,255,0.07)',
    color: C.accent,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    transition: 'background 0.2s',
  },

  // Feedback
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: C.red,
    marginTop: 10,
    fontSize: 13,
    padding: '10px 14px',
    borderRadius: 10,
    background: C.redDim,
    border: `1px solid rgba(248,113,113,0.2)`,
  },

  // Layout helpers
  row: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  divider: { height: 1, background: C.border, margin: '18px 0' },

  // Badge
  badge: (color) => {
    const map = {
      green:  { bg: C.greenDim,  text: C.green },
      red:    { bg: C.redDim,    text: C.red },
      blue:   { bg: C.accentDim, text: C.accent },
      gray:   { bg: 'rgba(255,255,255,0.07)', text: C.textSub },
      yellow: { bg: C.yellowDim, text: C.yellow },
      purple: { bg: C.purpleDim, text: C.purple },
    };
    const t = map[color] || map.gray;
    return {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 10px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: t.bg,
      color: t.text,
      whiteSpace: 'nowrap',
    };
  },

  // Progress bar
  progressBar: (pct) => ({
    height: 5,
    borderRadius: 999,
    background: `linear-gradient(90deg, #4fd1ff ${pct}%, rgba(255,255,255,0.07) ${pct}%)`,
    transition: 'background 0.5s ease',
    boxShadow: pct > 0 ? '0 0 10px rgba(79,209,255,0.3)' : 'none',
  }),

  // Score color
  scoreColor: (s) => s >= 8 ? C.green : s >= 6 ? C.yellow : C.red,

  // Stat box
  statBox: {
    padding: '14px 16px',
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: 'rgba(255,255,255,0.02)',
  },

  // Step indicator dot
  stepDot: (active, done) => ({
    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: done ? 14 : 13, fontWeight: 700,
    background: done ? 'rgba(74,222,128,0.15)' : active ? C.accentGrad : 'rgba(255,255,255,0.05)',
    border: done ? '1.5px solid rgba(74,222,128,0.4)' : active ? 'none' : `1.5px solid ${C.border}`,
    color: done ? C.green : active ? '#03101f' : C.textMuted,
    transition: 'all 0.3s',
  }),

  stepLine: (done) => ({
    flex: 1, height: 2, borderRadius: 999,
    background: done ? 'rgba(74,222,128,0.35)' : C.border,
    transition: 'background 0.4s',
  }),
};

